/**
 * Context that bridges the form-aware slide layer and the editor toolbar.
 *
 * The toolbar (`SlideEditorBodyToolbar`) is a Lexical plugin instantiated via
 * a `useMemo` factory — it cannot accept new props without breaking
 * memoization and causing the editor to remount. This context allows the
 * toolbar's prompt popover to read and write prompt state without any changes
 * to the shared `TextEditor` component or the toolbar plugin factory.
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUnguardedClientPresentationDesignerContext } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.context';
import { GeneratePresentationCustomSlidePromptInput } from '@/types/schema';

import {
  GenerationStatus,
  useGenerateCustomSlideContent,
} from '../customSlide/hooks/useGenerateCustomSlideContent';
import { usePresentationSystemPromptsQuery } from './graphql/PresentationSystemPrompts.generated';
import { SYSTEM_PROMPT_DISPLAY_NAMES } from './SlideEditor.promptPopoverConstants';

/** A built-in system prompt available as a preset in the prompt popover. */
export interface SystemPromptOption {
  /** Stable backend key (e.g. `EXECUTIVE_SUMMARY`). */
  key: string;
  /** Display name from the local [[SYSTEM_PROMPT_DISPLAY_NAMES]] map. */
  displayName: string;
  /** Current prompt text resolved by the backend. */
  prompt: string;
}

export interface SlideEditorPromptContextValue {
  customPrompt: string | null;
  /** Current persisted slide body content. Sent as `currentSlideContent` when
   *  refining existing output via the regenerate flow. */
  pageContent: string;
  onPromptChange: (prompt: string | null) => void;
  /** When true, show template-specific copy (e.g. footnote about generated text) */
  isTemplate: boolean;
  /** Incremented when a prompt is applied from the popover (not from editor edits). */
  promptApplyVersion: number;
  /** Call after applying a prompt from the popover to signal the RTE to remount. */
  notifyPromptApplied: () => void;
  /** Current status of AI content generation */
  generationStatus: GenerationStatus;
  /** Accumulated streamed text during generation (for real-time display) */
  streamedContent: string;
  /** Start AI content generation. Only works in non-template (presentation) mode. */
  startGeneration: (input: GeneratePresentationCustomSlidePromptInput) => void;
  /** Cancel an in-progress generation */
  cancelGeneration: () => void;
  /** When non-null, the AI generated content awaiting accept/reject. The
   *  editor renders this as its current content so the user can refine it
   *  before persisting. Only set in non-template (presentation) mode. */
  pendingContent: string | null;
  /** Editor hook: report the latest content while a pending generation is
   *  staged. Accept will persist whatever value was last reported. No-op when
   *  there is no pending content. */
  updatePendingContentDraft: (content: string) => void;
  /** Commit the pending generated content (including any inline edits the
   *  user has made) via `onContentGenerated`. No-op when there is no pending
   *  content. */
  acceptPendingContent: () => void;
  /** Discard the pending generated content; the editor will revert to the
   *  content it had before generation. No-op when there is no pending
   *  content. */
  rejectPendingContent: () => void;
  /** Built-in system prompts available as presets in the popover, filtered
   *  to those whose `key` appears in [[SYSTEM_PROMPT_DISPLAY_NAMES]]. */
  systemPrompts: SystemPromptOption[];
}

const SlideEditorPromptContext =
  createContext<SlideEditorPromptContextValue | null>(null);

/**
 * Lightweight context that marks the current tree as a template editor.
 * The default value is `false`; only the template slide editor provides `true`.
 */
const IsTemplateContext = createContext(false);

/**
 * Wrap a template editor subtree in this provider so that prompt UI
 * can detect template mode without prop-drilling.
 */
export function IsTemplateProvider({ children }: PropsWithChildren) {
  return (
    <IsTemplateContext.Provider value={true}>
      {children}
    </IsTemplateContext.Provider>
  );
}

interface SlideEditorPromptProviderProps {
  customPrompt: string | null;
  /** Current persisted slide body content. Exposed via context so the prompt
   *  popover can send it as `currentSlideContent` for the refine-output flow. */
  pageContent: string;
  onPromptChange: (prompt: string | null) => void;
  /** Callback to persist generated content to the form. Required for
   *  non-template (presentation) mode where generation writes to pageContent. */
  onContentGenerated?: (content: string) => void;
}

export function SlideEditorPromptProvider({
  customPrompt,
  pageContent,
  onPromptChange,
  onContentGenerated,
  children,
}: PropsWithChildren<SlideEditorPromptProviderProps>) {
  const isTemplate = useContext(IsTemplateContext);
  const [promptApplyVersion, setPromptApplyVersion] = useState(0);
  const notifyPromptApplied = useCallback(
    () => setPromptApplyVersion((v) => v + 1),
    []
  );

  // `pendingContent` state seeds the editor when generation completes and
  // signals pending mode to consumers. The latest edits the user makes to
  // that content live in `latestPendingContentRef` so we don't re-render the
  // tree on every keystroke. Accept reads the ref to persist the final value.
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  const latestPendingContentRef = useRef<string | null>(null);

  // Mirror the latest onContentGenerated in a ref. The Accept handler is
  // exposed via a stable useCallback (so consumers can pass it to memoized
  // children without churn); reading the ref ensures Accept always invokes
  // the freshest persistence callback.
  const onContentGeneratedRef = useRef(onContentGenerated);
  onContentGeneratedRef.current = onContentGenerated;

  const {
    startGeneration: startGenerationInternal,
    cancelGeneration,
    status: generationStatus,
    streamedContent,
  } = useGenerateCustomSlideContent({
    onComplete: (generatedContent) => {
      if (isTemplate) {
        // Template prompts have no accept/reject step; write immediately
        // and remount so the editor picks up the new content.
        onContentGeneratedRef.current?.(generatedContent);
        setPromptApplyVersion((v) => v + 1);
        return;
      }
      // Non-template: stage as pending so the user can refine, accept, or
      // reject before anything hits the form.
      latestPendingContentRef.current = generatedContent;
      setPendingContent(generatedContent);
      setPromptApplyVersion((v) => v + 1);
    },
  });

  const updatePendingContentDraft = useCallback((content: string) => {
    // Only update when a pending generation is staged. Guards against
    // stray editor updates after accept/reject clears the ref.
    if (latestPendingContentRef.current != null) {
      latestPendingContentRef.current = content;
    }
  }, []);

  const acceptPendingContent = useCallback(() => {
    const final = latestPendingContentRef.current;
    if (final == null) return;
    onContentGeneratedRef.current?.(final);
    latestPendingContentRef.current = null;
    setPendingContent(null);
    // No promptApplyVersion bump: the editor blurs on the pending→idle
    // transition, so the TextEditor unmounts naturally. Bumping the
    // version would cause a brief remount with the staged content before
    // the blur landed.
  }, []);

  const rejectPendingContent = useCallback(() => {
    if (latestPendingContentRef.current == null) return;
    latestPendingContentRef.current = null;
    setPendingContent(null);
  }, []);

  // Sync streaming status and cancel function to the presentation designer's
  // loading overlay. When this provider lives inside a
  // ClientPresentationDesignerProvider, the overlay will show during
  // generation. In template mode (or outside the designer), the context
  // defaults are no-ops and this is harmless.
  const designerCtx = useUnguardedClientPresentationDesignerContext();
  useEffect(() => {
    designerCtx.setIsCustomSlideGenerating(generationStatus === 'generating');
  }, [designerCtx, generationStatus]);
  useEffect(() => {
    designerCtx.setCancelCustomSlideGeneration(cancelGeneration);
  }, [designerCtx, cancelGeneration]);

  const startGeneration = useCallback(
    (input: GeneratePresentationCustomSlidePromptInput) => {
      startGenerationInternal(input);
    },
    [startGenerationInternal]
  );

  // Fetch the registry of built-in system prompts. Apollo's cache dedupes
  // across slide-editor instances mounted in the same session, so this query
  // is effectively fired once per page load.
  const { data: systemPromptsData } = usePresentationSystemPromptsQuery();
  const systemPrompts = useMemo<SystemPromptOption[]>(() => {
    const prompts = systemPromptsData?.presentationSystemPrompts;
    if (!prompts) return [];
    return prompts.flatMap((p) => {
      const displayName = SYSTEM_PROMPT_DISPLAY_NAMES[p.key];
      if (!displayName) return [];
      return [{ key: p.key, displayName, prompt: p.prompt }];
    });
  }, [systemPromptsData]);

  const value = useMemo(
    () => ({
      customPrompt,
      pageContent,
      onPromptChange,
      isTemplate,
      promptApplyVersion,
      notifyPromptApplied,
      generationStatus,
      streamedContent,
      startGeneration,
      cancelGeneration,
      pendingContent,
      updatePendingContentDraft,
      acceptPendingContent,
      rejectPendingContent,
      systemPrompts,
    }),
    [
      customPrompt,
      pageContent,
      onPromptChange,
      isTemplate,
      promptApplyVersion,
      notifyPromptApplied,
      generationStatus,
      streamedContent,
      startGeneration,
      cancelGeneration,
      pendingContent,
      updatePendingContentDraft,
      acceptPendingContent,
      rejectPendingContent,
      systemPrompts,
    ]
  );

  return (
    <SlideEditorPromptContext.Provider value={value}>
      {children}
    </SlideEditorPromptContext.Provider>
  );
}

/**
 * Returns the prompt context value, or null when no provider exists.
 * This allows the toolbar to work in both prompt-enabled and non-prompt contexts.
 */
export function useSlideEditorPromptContext(): SlideEditorPromptContextValue | null {
  return useContext(SlideEditorPromptContext);
}

/**
 * Returns whether the current subtree is inside a template editor.
 * Defaults to `false` when no `IsTemplateProvider` exists above.
 */
export function useIsTemplate(): boolean {
  return useContext(IsTemplateContext);
}

/**
 * Derives the display content, editable content, and submit handler for a
 * slide RTE that may have an active AI prompt.
 *
 * When a prompt is active (template + non-empty customPrompt):
 *   - `displayContent` wraps the prompt text for the read-only preview
 *   - `editableContent` is the raw prompt text loaded into the editor
 *   - `handleRTESubmit` routes editor saves to `onPromptChange`
 *
 * Otherwise all three pass through to the normal content / change handler.
 */
export function usePromptDisplayContent({
  content,
  customPrompt,
  onContentChange,
  onPromptChange,
}: {
  content: string;
  customPrompt: string | null | undefined;
  onContentChange?: (content: string) => void;
  onPromptChange?: (prompt: string | null) => void;
}) {
  const isTemplate = useIsTemplate();
  const hasActivePrompt = isTemplate && !!customPrompt;

  const displayContent = hasActivePrompt
    ? `{Prompt}\n${customPrompt}\n{End prompt}`
    : content;
  const editableContent = hasActivePrompt ? customPrompt : undefined;
  // Normalize empty/whitespace to null so clearing text in the editor clears the prompt.
  const handleRTESubmit = hasActivePrompt
    ? onPromptChange
      ? (text: string) => onPromptChange(text.trim() || null)
      : undefined
    : onContentChange;

  return { displayContent, editableContent, handleRTESubmit };
}
